import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import {
  Alert, Avatar, Box, Button, Chip, Grid, Paper, Snackbar, TextField, Typography, alpha,
} from '@mui/material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import { getAuthProfile, updateAuthProfile } from '../../services/authService';
import { selectUser, updateUser } from '../../store/slices/authSlice';
import { getRoleLabel } from '../../config/roleNavigation';
import { getApiErrorMessage } from '../../services/userService';
import PersonNameField from '../../components/ui/forms/PersonNameField';
import { validatePersonName } from '../../utils/personFieldValidation';

export default function AccountSettingsPage() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const sessionUser = useSelector(selectUser);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [snack, setSnack] = useState('');
  const [error, setError] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['auth-profile'],
    queryFn: getAuthProfile,
  });

  useEffect(() => {
    if (!data) return;
    setFirstName(data.firstName || '');
    setLastName(data.lastName || '');
    setEmail(data.email || '');
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const firstErr = validatePersonName(firstName, 'First name', { required: true });
      const lastErr = validatePersonName(lastName, 'Last name');
      if (firstErr || lastErr) {
        return Promise.reject(new Error(firstErr || lastErr));
      }
      return updateAuthProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim() || undefined,
      });
    },
    onSuccess: (profile) => {
      dispatch(updateUser({
        firstName: profile.firstName,
        lastName: profile.lastName,
        email: profile.email,
      }));
      queryClient.invalidateQueries({ queryKey: ['auth-profile'] });
      setSnack('Profile updated successfully');
      setError('');
    },
    onError: (err) => setError(getApiErrorMessage(err)),
  });

  const displayName = [firstName || data?.firstName, lastName || data?.lastName].filter(Boolean).join(' ') || 'Account';
  const roleLabel = getRoleLabel(data?.userType || sessionUser?.userType);

  return (
    <PageShell>
      <PageHeader
        title="My Account"
        subtitle="Update your name and contact details"
      />

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Paper
            elevation={0}
            sx={{
              p: 3,
              borderRadius: 3,
              border: 1,
              borderColor: 'divider',
              textAlign: 'center',
            }}
          >
            <Avatar
              sx={{
                width: 72,
                height: 72,
                mx: 'auto',
                mb: 1.5,
                fontSize: '1.75rem',
                fontWeight: 800,
              }}
            >
              {(data?.firstName || sessionUser?.firstName || '?')[0]}
            </Avatar>
            <Typography variant="h6" fontWeight={800}>{displayName}</Typography>
            <Typography variant="body2" color="text.secondary" mt={0.5}>
              {data?.email || sessionUser?.email || data?.phone || sessionUser?.phone || '—'}
            </Typography>
            {roleLabel && (
              <Chip label={roleLabel} size="small" sx={{ mt: 1.5, fontWeight: 700 }} />
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={8}>
          <Paper elevation={0} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 3, border: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={800} mb={2}>Profile details</Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <PersonNameField
                  fullWidth
                  label="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isLoading || saveMutation.isPending}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <PersonNameField
                  fullWidth
                  label="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isLoading || saveMutation.isPending}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={isLoading || saveMutation.isPending}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone"
                  value={data?.phone || sessionUser?.phone || ''}
                  disabled
                  helperText="Contact support to change your phone number"
                />
              </Grid>
            </Grid>

            <Box
              mt={3}
              pt={2}
              borderTop={1}
              borderColor="divider"
              display="flex"
              justifyContent="flex-end"
              gap={1.5}
            >
              <Button
                variant="contained"
                disabled={isLoading || saveMutation.isPending || !firstName.trim()}
                onClick={() => saveMutation.mutate()}
              >
                {saveMutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </Box>

            <Box
              mt={3}
              p={2}
              borderRadius={2}
              sx={{ bgcolor: (theme) => alpha(theme.palette.info.main, 0.06), border: 1, borderColor: 'divider' }}
            >
              <Typography variant="caption" color="text.secondary">
                Account type: <strong>{roleLabel || 'User'}</strong>
                {sessionUser?.email ? ` · Signed in as ${sessionUser.email}` : ''}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')} message={snack} />
    </PageShell>
  );
}
